<template>
  <article class="sheet-selection-card">
    <div class="sheet-selection-card__image-section">
      <img
        :src="thumbnailUrl"
        :alt="displayName"
        class="sheet-selection-card__image"
        loading="lazy"
      />
    </div>
    <div class="sheet-selection-card__content">
      <p class="sheet-selection-card__name">{{ displayName }}</p>
      <div class="sheet-selection-card__controls">
        <div
          class="sheet-selection-card__stepper"
          role="group"
          :aria-label="stepperAriaLabel"
        >
          <button
            type="button"
            class="sheet-selection-card__stepper-btn"
            :disabled="quantity === 0"
            @click="handleDecrement"
            :aria-label="decrementAriaLabel"
          >
            <FontAwesomeIcon :icon="appIcons.faMinus" />
          </button>
          <span
            class="sheet-selection-card__stepper-count"
            aria-live="polite"
          >
            {{ quantity }}
          </span>
          <button
            type="button"
            class="sheet-selection-card__stepper-btn"
            :disabled="incrementDisabled"
            @click="handleIncrement"
            :aria-label="incrementAriaLabel"
          >
            <FontAwesomeIcon :icon="appIcons.faPlus" />
          </button>
        </div>
        <button
          type="button"
          class="sheet-selection-card__trash-btn"
          :disabled="quantity === 0"
          @click="handleClear"
          :aria-label="clearAriaLabel"
        >
          <FontAwesomeIcon :icon="appIcons.farTrashCan" />
        </button>
      </div>
    </div>
  </article>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { useCatalogStore } from '@/stores/catalog'
import { usePackageBuilderStore } from '@/stores/packageBuilder'
import { mapActions, mapState, mapStores } from 'pinia'

import useAppIcons from '@/composables/appIcons'

import {
  type BuilderContent,
  resolveBuilderContent,
  substitute
} from '@/components/Senior/PackageBuilder/content'

import type { ItemGroupItemStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

// Placeholder product imagery for the polish pass. Real catalog imagery (per
// `item.canvasLayout` / `item.attributes`) is a separate follow-up — see the
// Package Builder IG follow-ups section. `placehold.co` renders a flat gray
// box with the URL-supplied text overlaid; keeps QA visuals unambiguous.
const PLACEHOLDER_IMAGE_BASE = 'https://placehold.co'
const PLACEHOLDER_IMAGE_DIMENSIONS = '400x500'

export default defineComponent({
  name: 'SheetSelectionCard',
  props: {
    itemGroupItem: {
      type: Object as PropType<ItemGroupItemStorefrontDto>,
      required: true
    },
    // The cart bundle whose sheet selection this card edits.
    cartBundleId: {
      type: String,
      required: true
    }
  },
  setup() {
    return { ...useAppIcons() }
  },
  computed: {
    ...mapStores(useAppContentStore, useCatalogStore),
    ...mapState(usePackageBuilderStore, [
      'selectionsByCartBundle',
      'totalSelectedForCartBundle',
      'targetCountForCartBundle'
    ]),
    content(): BuilderContent {
      return resolveBuilderContent(this.appContentStore.seniorPackageBuilderContent)
    },
    quantity(): number {
      return (
        this.selectionsByCartBundle[this.cartBundleId]?.[this.itemGroupItem.itemGroupItemId] ?? 0
      )
    },
    displayName(): string {
      return (
        this.catalogStore.catalogItemContent(this.itemGroupItem.itemId)?.retailName ||
        this.itemGroupItem.item?.retailName ||
        this.itemGroupItem.item?.nameInternal ||
        '##sheet-name##'
      )
    },
    incrementDisabled(): boolean {
      return (
        this.totalSelectedForCartBundle(this.cartBundleId) >=
        this.targetCountForCartBundle(this.cartBundleId)
      )
    },
    thumbnailUrl(): string {
      const imageUrl = this.catalogStore.catalogItemContent(this.itemGroupItem.itemId)
        ?.sheetSelectionImage?.file?.url
      return imageUrl
        ? imageUrl
        : `${PLACEHOLDER_IMAGE_BASE}/${PLACEHOLDER_IMAGE_DIMENSIONS}?text=${encodeURIComponent(this.displayName)}`
    },
    stepperAriaLabel(): string {
      return substitute(this.content.stepperGroupAriaLabelTemplate, {
        SheetName: this.displayName
      })
    },
    decrementAriaLabel(): string {
      return this.content.stepperDecrementAriaLabel
    },
    incrementAriaLabel(): string {
      return this.content.stepperIncrementAriaLabel
    },
    clearAriaLabel(): string {
      return substitute(this.content.clearAriaLabelTemplate, { SheetName: this.displayName })
    }
  },
  methods: {
    ...mapActions(usePackageBuilderStore, [
      'incrementSheetForCartBundle',
      'decrementSheetForCartBundle',
      'clearSheetForCartBundle'
    ]),
    handleIncrement() {
      this.incrementSheetForCartBundle(this.cartBundleId, this.itemGroupItem.itemGroupItemId)
    },
    handleDecrement() {
      this.decrementSheetForCartBundle(this.cartBundleId, this.itemGroupItem.itemGroupItemId)
    },
    handleClear() {
      this.clearSheetForCartBundle(this.cartBundleId, this.itemGroupItem.itemGroupItemId)
    }
  }
})
</script>

<style scoped lang="scss">
.sheet-selection-card {
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 12px;
  // Figma "Elevation 3" (node 9196:234094) — the same pair `BundleListItemFeatured`
  // uses, replacing this card's earlier bespoke shadow.
  box-shadow:
    0 4px 8px 0 rgba(0, 0, 0, 0.12),
    0 0 2px 0 rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &__image-section {
    // Figma's 272 × 233 image box.
    aspect-ratio: 272 / 233;
    overflow: hidden;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__content {
    display: flex;
    flex-direction: column;
    padding: 0.625rem;
    gap: 0.5rem;
  }

  &__name {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.75rem;
    color: #000;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__stepper {
    display: inline-flex;
    align-items: center;
    background: #000;
    color: #fff;
    border-radius: 999px;
    padding: 0.25rem 0.5rem;
    gap: 0.25rem;
  }

  &__stepper-btn {
    background: transparent;
    border: none;
    color: #fff;
    width: 2rem;
    height: 2rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.12);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }

  &__stepper-count {
    color: #fff;
    font-weight: 600;
    font-size: 1rem;
    min-width: 1.25rem;
    text-align: center;
  }

  &__trash-btn {
    background: transparent;
    border: none;
    color: #000;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    border-radius: 50%;
    margin-left: auto;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  @include media-breakpoint-up(md) {
    &__content {
      padding: 1rem;
      gap: 0.75rem;
    }
  }

  @include media-breakpoint-up(lg) {
    &__content {
      padding: 1.25rem;
    }
  }
}
</style>
